import {
  normalizeLabel,
  parseNumeric,
  postRequest,
} from '../../../shared/utils';
import { resolveRoleRecipientsByProfitCenter } from './notificationRecipients';

const NEED_ROLE_MAP = {
  inspection_planning_need: 'PP',
  ncpneed: 'NCP',
  cmmneed: 'CMMP',
  qsfaneed: 'QSFA',
} as const;

type NeedField = keyof typeof NEED_ROLE_MAP;

type NeedRole = (typeof NEED_ROLE_MAP)[NeedField];

type NpiSteeringAvRow = {
  id1: number | string;
  pf_lc?: number | string | null;
  inspection_planning_need?: number | string | null;
  ncpneed?: number | string | null;
  cmmneed?: number | string | null;
  qsfaneed?: number | string | null;
};

type ProfitCenterOption = { label: string; value: number };
type NeedOption = { label: string; value: number };

type CreateAvToDownstreamHandoffNotificationsParams = {
  row: NpiSteeringAvRow;
  screenGridId: number;
  createdBy: number;
  valueOptionsProfitCenter: ProfitCenterOption[];
  valueOptionsNeed: NeedOption[];
  configSchema: string;
  needFields?: NeedField[];
};

type NotificationRequest = {
  roleName: NeedRole;
  needField: NeedField;
  messageKey: string;
  message: string;
};

const NOTIFICATION_MESSAGES_BY_ROLE: Record<NeedRole, string> = {
  PP: 'Part with NPI ID {{id1}} is ready for QP. Profit Center: {{pf_lc}}',
  NCP: 'Part with NPI ID {{id1}} is ready for NCP. Profit Center: {{pf_lc}}',
  CMMP: 'Part with NPI ID {{id1}} is ready for CMM. Profit Center: {{pf_lc}}',
  QSFA: 'Part with NPI ID {{id1}} is ready for QSFA. Profit Center: {{pf_lc}}',
};

const NOTIFICATION_MESSAGE_KEYS_BY_ROLE: Record<NeedRole, string> = {
  PP: 'npiSteering.av_to_qp_message',
  NCP: 'npiSteering.av_to_ncp_message',
  CMMP: 'npiSteering.av_to_cmmp_message',
  QSFA: 'npiSteering.av_to_qsfa_message',
};

function isNeedSelectedYes(value: unknown, yesNeedId: number | null): boolean {
  const parsed = parseNumeric(value);
  return yesNeedId != null && parsed != null && parsed === yesNeedId;
}

function interpolate(template: string, params: { id1: string; pf_lc: string }): string {
  return template.replace('{{id1}}', params.id1).replace('{{pf_lc}}', params.pf_lc);
}

function resolveYesNeedId(valueOptionsNeed: NeedOption[]): number | null {
  const yesOption = valueOptionsNeed.find((option) => option.label?.toLowerCase() === 'yes');
  return yesOption?.value ?? null;
}

function resolveInspectionPlanningTriggerIds(valueOptionsNeed: NeedOption[]): Set<number> {
  const triggerLabels = new Set(['full fai', 'delta fai']);
  return new Set(
    valueOptionsNeed
      .filter((option) => triggerLabels.has(normalizeLabel(option.label)))
      .map((option) => option.value),
  );
}

function buildNotificationRequests(
  row: NpiSteeringAvRow,
  yesNeedId: number | null,
  inspectionPlanningTriggerIds: Set<number>,
  params: { id1: string; pf_lc: string },
  needFields?: NeedField[],
): NotificationRequest[] {
  const fields = Object.keys(NEED_ROLE_MAP) as NeedField[];
  const allowedNeedFields = needFields ? new Set(needFields) : null;

  return fields
    .filter((field) => {
      if (allowedNeedFields && !allowedNeedFields.has(field)) {
        return false;
      }

      if (field === 'inspection_planning_need') {
        const inspectionNeedId = parseNumeric(row[field]);
        return inspectionNeedId != null && inspectionPlanningTriggerIds.has(inspectionNeedId);
      }

      return isNeedSelectedYes(row[field], yesNeedId);
    })
    .map((field) => {
      const roleName = NEED_ROLE_MAP[field];
      const messageTemplate = NOTIFICATION_MESSAGES_BY_ROLE[roleName];
      const messageKey = NOTIFICATION_MESSAGE_KEYS_BY_ROLE[roleName];

      return {
        roleName,
        needField: field,
        messageKey,
        message: interpolate(messageTemplate, params),
      };
    });
}

export async function createAvToDownstreamHandoffNotifications({
  row,
  screenGridId,
  createdBy,
  valueOptionsProfitCenter,
  valueOptionsNeed,
  configSchema,
  needFields,
}: CreateAvToDownstreamHandoffNotificationsParams): Promise<void> {
  const rowId = parseNumeric(row.id1);
  const pcnrId = parseNumeric(row.pf_lc);

  if (rowId == null || pcnrId == null) {
    return;
  }

  const yesNeedId = resolveYesNeedId(valueOptionsNeed);
  const pcnrLabel =
    valueOptionsProfitCenter.find((option) => option.value === pcnrId)?.label ?? String(pcnrId);

  const params = {
    id1: String(rowId),
    pf_lc: pcnrLabel,
  };

  const inspectionPlanningTriggerIds = resolveInspectionPlanningTriggerIds(valueOptionsNeed);

  const requests = buildNotificationRequests(
    row,
    yesNeedId,
    inspectionPlanningTriggerIds,
    params,
    needFields,
  );
  if (!requests.length) {
    return;
  }

  await Promise.all(
    requests.map(async (request) => {
      const userIds = await resolveRoleRecipientsByProfitCenter({
        screenGridId,
        pcnrId,
        roleName: request.roleName,
        configSchema,
      });

      if (!userIds.length) {
        return;
      }

      await postRequest('/api/notification/createNotification', {
        type: 'AV_TO_DOWNSTREAM_HANDOFF',
        payload: {
          screen: 'NPI_STEERING',
          rowId,
          pkey: 'id1',
          schemaName: 'npi',
          tableName: 'sheet1',
          screenGridId,
          roleName: request.roleName,
          needField: request.needField,
          messageKey: request.messageKey,
          messageParams: params,
          actionConfig: {
            allowReject: true,
          },
        },
        createdBy,
        userIds,
        status: 'UNREAD',
        message: request.message,
      });
    }),
  );
}
